import { useState, useEffect, useMemo } from 'react';
import { formatINR } from '@/utils/format';
import bomAPI from '@/api/bom.api';

interface PrintItem {
  id: string;
  parentId: string | null;
  itemName: string;
  itemCode: string;
  qtyPerUnit: number;
  unit: string;
  scrapPct: number;
  level: number;
  itemType: string;
  standardCost?: number;
}

interface BOMPrintModalProps {
  isOpen: boolean;
  onClose: () => void;
  bom: any;
  customItems?: PrintItem[];
}

export default function BOMPrintModal({
  isOpen,
  onClose,
  bom,
  customItems,
}: BOMPrintModalProps) {
  const [fetchedBom, setFetchedBom] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen || !bom?.id || bom.id === 'new-bom') {
      setFetchedBom(null);
      return;
    }

    // If items missing, fetch full BOM details from server
    const hasItems = (bom.items && Array.isArray(bom.items) && bom.items.length > 0) || (customItems && customItems.length > 0);
    if (!hasItems) {
      setLoading(true);
      bomAPI.getById(bom.id)
        .then((res: any) => {
          const data = res?.data || res;
          if (data && (data.id || data.items || data.code)) {
            setFetchedBom(data);
          }
        })
        .catch((err) => {
          console.error('Failed to load BOM print details:', err);
        })
        .finally(() => setLoading(false));
    }
  }, [isOpen, bom?.id, bom?.items, customItems]);

  const activeBom = useMemo(() => {
    return {
      ...(bom || {}),
      ...(fetchedBom || {}),
    };
  }, [bom, fetchedBom]);

  const bomItems = useMemo(() => {
    if (customItems && customItems.length > 0) {
      return customItems.map((bi: any) => ({
        id: bi.id,
        parentId: bi.parentId || bi.parent_id || null,
        itemName: bi.itemName || bi.item_name || 'Unknown Item',
        itemCode: bi.itemCode || bi.item_code || '',
        qtyPerUnit: Number(bi.qtyPerUnit || bi.effective_qty || bi.quantity || 1),
        unit: bi.unit || 'Pcs',
        scrapPct: Number(bi.scrapPct || bi.scrap_pct || 0),
        level: Number(bi.level ?? 1),
        itemType: bi.itemType || bi.item_type || 'RAW_MATERIAL',
        standardCost: Number(bi.standardCost || bi.standard_cost || bi.purchase_rate || bi.rate || 0),
      })).filter((bi: any) => bi.level > 0);
    }

    const itemsSource = activeBom?.items || [];
    if (Array.isArray(itemsSource)) {
      return itemsSource.map((bi: any) => ({
        id: bi.id,
        parentId: bi.parent_id || bi.parentId || null,
        itemName: bi.item_name || bi.itemName || 'Unknown Item',
        itemCode: bi.item_code || bi.itemCode || '',
        qtyPerUnit: Number(bi.effective_qty || bi.qty_per_unit || bi.quantity || 1),
        unit: bi.unit_of_measure || bi.unit_name || bi.unit || 'Pcs',
        scrapPct: Number(bi.scrap_percentage || bi.scrap_pct || bi.scrapPct || 0),
        level: Number(bi.level ?? 1),
        itemType: bi.item_type || bi.itemType || 'RAW_MATERIAL',
        standardCost: Number(bi.standard_cost || bi.standardCost || bi.purchase_rate || bi.rate || 0),
      })).sort((a: any, b: any) => a.level - b.level);
    }
    return [];
  }, [activeBom, customItems]) as PrintItem[];

  const itemIdsSet = useMemo(() => new Set(bomItems.map((b) => b.id)), [bomItems]);

  // Build parent→children map for tree display
  const childrenMap = useMemo(() => {
    const map = new Map<string, PrintItem[]>();
    bomItems.forEach((item) => {
      if (item.parentId && itemIdsSet.has(item.parentId)) {
        if (!map.has(item.parentId)) map.set(item.parentId, []);
        map.get(item.parentId)!.push(item);
      }
    });
    return map;
  }, [bomItems, itemIdsSet]);

  const roots = useMemo(() => {
    const list = bomItems.filter((bi) => !bi.parentId || !itemIdsSet.has(bi.parentId));
    if (list.length === 0 && bomItems.length > 0) {
      const minLevel = Math.min(...bomItems.map((b) => b.level));
      return bomItems.filter((b) => b.level === minLevel);
    }
    return list;
  }, [bomItems, itemIdsSet]);

  const variantTags = useMemo(() => {
    const attrs = activeBom?.variantAttributes || activeBom?.variant_attributes;
    if (!attrs) return [];
    return Object.entries(attrs).map(
      ([k, v]) => `${k}: ${v}`,
    );
  }, [activeBom]);

  const totalCost = useMemo(() => {
    const directCost = Number(
      activeBom?.totalMaterialCost ??
      activeBom?.total_material_cost ??
      activeBom?.materialCost ??
      activeBom?.material_cost ??
      0
    );
    if (directCost > 0) return directCost;

    const fromItems = bomItems.reduce((sum, item) => {
      const rate = Number(item.standardCost || 0);
      const effQty = item.qtyPerUnit * (1 + (item.scrapPct || 0) / 100);
      return sum + (rate * effQty);
    }, 0);
    return fromItems;
  }, [activeBom, bomItems]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 print:hidden">
          <h2 className="text-lg font-bold text-[#1e293b]">Print BOM</h2>
          <div className="flex gap-2">
            <button
              onClick={() => window.print()}
              className="h-9 px-4 rounded-lg bg-[#4f46e5] text-white text-sm font-semibold hover:bg-[#4338ca] cursor-pointer whitespace-nowrap flex items-center gap-1.5"
            >
              <i className="ri-printer-line" />
              Print
            </button>
            <button
              onClick={onClose}
              className="h-9 px-4 rounded-lg border border-[#e2e8f0] text-sm font-medium text-[#64748b] hover:bg-[#f8fafc] cursor-pointer whitespace-nowrap"
            >
              Close
            </button>
          </div>
        </div>

        {/* Print preview */}
        <div className="flex-1 overflow-auto p-8" id="bom-print-content">
          <div className="max-w-2xl mx-auto">
            {/* Title */}
            <div className="text-center mb-6">
              <h1 className="text-xl font-bold text-[#1e293b] uppercase tracking-wider">
                Bill of Materials
              </h1>
              <div className="w-16 h-0.5 bg-[#1e293b] mx-auto mt-2" />
            </div>

            {loading ? (
              <div className="py-16 text-center text-slate-500">
                <i className="ri-loader-4-line animate-spin text-3xl mb-3 text-indigo-600 block" />
                <p className="text-sm font-medium">Loading BOM details...</p>
              </div>
            ) : (
              <>
                {/* BOM Header Info */}
                <div className="mb-6 space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Product:</span>
                    <span className="font-semibold text-[#1e293b]">
                      {activeBom.productName || activeBom.product_name || '—'}
                    </span>
                  </div>
                  {activeBom.isVariantBOM && activeBom.variantName && (
                    <>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Variant:</span>
                        <span className="font-semibold text-[#1e293b]">
                          {activeBom.variantName}
                        </span>
                      </div>
                      {variantTags.length > 0 && (
                        <div className="flex justify-between">
                          <span className="text-slate-500">Attributes:</span>
                          <span className="font-medium text-[#1e293b]">
                            {variantTags.join(' · ')}
                          </span>
                        </div>
                      )}
                    </>
                  )}
                  <div className="flex justify-between">
                    <span className="text-slate-500">BOM Code:</span>
                    <span className="font-medium text-[#1e293b]">{activeBom.code || '—'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Version:</span>
                    <span className="font-medium text-[#1e293b]">
                      v{activeBom.version || '1.0'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Status:</span>
                    <span className="font-medium text-[#1e293b]">{activeBom.status || 'ACTIVE'}</span>
                  </div>
                  {/* Quantities on this sheet are for the BOM's base quantity */}
                  <div className="flex justify-between">
                    <span className="text-slate-500">Base Qty:</span>
                    <span className="font-medium text-[#1e293b]">
                      {Number((activeBom as any).baseQty ?? (activeBom as any).base_qty) > 0 ? Number((activeBom as any).baseQty ?? (activeBom as any).base_qty) : 1}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Effective From:</span>
                    <span className="font-medium text-[#1e293b]">
                      {activeBom.effectiveFrom || activeBom.effective_from || '—'}
                    </span>
                  </div>
                  {activeBom.notes && (
                    <div className="flex justify-between">
                      <span className="text-slate-500">Notes:</span>
                      <span className="font-medium text-[#1e293b] max-w-xs text-right">
                        {activeBom.notes}
                      </span>
                    </div>
                  )}
                </div>

                {/* Divider */}
                <div className="border-t-2 border-[#1e293b] mb-4" />

                {/* Tree Table */}
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b-2 border-[#1e293b]">
                      <th className="py-2 text-left font-semibold w-10">#</th>
                      <th className="py-2 text-left font-semibold">Item Name</th>
                      <th className="py-2 text-right font-semibold w-20">Qty</th>
                      <th className="py-2 text-left font-semibold w-16">Unit</th>
                      <th className="py-2 text-right font-semibold w-20">Scrap</th>
                    </tr>
                  </thead>
                  <tbody>
                    {roots.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="py-6 text-center text-slate-400">
                          No components found in this BOM
                        </td>
                      </tr>
                    ) : (
                      roots.map((root, idx) => (
                        <PrintTreeRow
                          key={root.id}
                          item={root}
                          index={idx + 1}
                          level={0}
                          childrenMap={childrenMap}
                        />
                      ))
                    )}
                  </tbody>
                </table>

                {/* Footer */}
                <div className="border-t-2 border-[#1e293b] mt-4 pt-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-500">
                      Total Material Cost
                    </span>
                    <span className="text-lg font-bold text-[#1e293b]">
                      {formatINR(totalCost)}
                    </span>
                  </div>
                </div>
              </>
            )}

            {/* Print footer */}
            <div className="mt-8 pt-4 border-t border-slate-200 text-xs text-slate-400 text-center">
              <p>Generated from InvenPro Solutions Pvt. Ltd.</p>
              <p>
                {new Date().toLocaleDateString('en-IN', {
                  day: '2-digit',
                  month: 'short',
                  year: 'numeric',
                })}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function PrintTreeRow({
  item,
  index,
  level,
  childrenMap,
}: {
  item: PrintItem;
  index: number;
  level: number;
  childrenMap: Map<string, PrintItem[]>;
}) {
  const children = childrenMap.get(item.id) || [];
  const indent = level * 24;
  const prefix = level > 0 ? '└ ' : '';

  return (
    <>
      <tr className="border-b border-slate-100">
        <td className="py-2 text-slate-500">{level === 0 ? index : ''}</td>
        <td className="py-2" style={{ paddingLeft: indent }}>
          <span
            className={
              level === 0 ? 'font-semibold text-[#1e293b]' : 'text-[#1e293b]'
            }
          >
            {prefix}
            {item.itemName}
          </span>
        </td>
        <td className="py-2 text-right font-medium">{item.qtyPerUnit}</td>
        <td className="py-2 text-slate-600">{item.unit}</td>
        <td className="py-2 text-right text-slate-500">
          {item.scrapPct > 0 ? `${item.scrapPct}%` : '—'}
        </td>
      </tr>
      {children.map((child, cidx) => (
        <PrintTreeRow
          key={child.id}
          item={child}
          index={cidx + 1}
          level={level + 1}
          childrenMap={childrenMap}
        />
      ))}
    </>
  );
}
